import type { ContentItemDto } from '@nova/shared';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFilter } from '../../app/FilterProvider.js';
import { NewContentDialog } from './NewContentDialog.js';

/**
 * Makes "Legg til nytt innlegg" reachable from every page: the dialog lives once
 * at the shell level, and any surface (the header button, an empty state) just
 * calls `useNewContent().open()`. A fresh item opens straight in the plan drawer.
 */
interface NewContentContextValue {
  open: () => void;
}

const NewContentContext = createContext<NewContentContextValue | null>(null);

export function NewContentProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { visibleAreas } = useFilter();
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const value = useMemo<NewContentContextValue>(() => ({ open }), [open]);

  return (
    <NewContentContext.Provider value={value}>
      {children}
      <NewContentDialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        areas={visibleAreas}
        onCreated={(item: ContentItemDto) => {
          setIsOpen(false);
          navigate(`/plan/${item.id}`);
        }}
      />
    </NewContentContext.Provider>
  );
}

export function useNewContent(): NewContentContextValue {
  const context = useContext(NewContentContext);
  if (!context) throw new Error('useNewContent må brukes inne i NewContentProvider.');
  return context;
}
